<template>
    <section
        class="flex flex-col rounded-xl border border-zinc-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    >
        <div
            class="flex items-center justify-between border-b border-zinc-200 px-4 py-3.5 dark:border-gray-700"
        >
            <span
                class="text-[15px] font-semibold text-zinc-950 dark:text-white"
            >
                Decisão
            </span>
            <span class="font-mono text-xs text-zinc-500 dark:text-gray-400">
                #{{ review.cashout?.id }}
            </span>
        </div>

        <div class="flex flex-col gap-3.5 px-4 py-3.5">
            <div
                v-if="suggestion"
                class="flex items-start gap-2.5 rounded-[10px] border border-zinc-200 bg-zinc-50 px-3 py-2.5 dark:border-gray-700 dark:bg-gray-900/40"
            >
                <SparklesIcon
                    class="mt-0.5 h-4 w-4 shrink-0 text-zinc-600 dark:text-gray-300"
                />
                <div class="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span
                        class="text-[13px] font-medium text-zinc-950 dark:text-white"
                    >
                        Sugestão: {{ suggestion.label }}
                        <span
                            v-if="suggestion.confidence !== null"
                            class="font-normal text-zinc-500 dark:text-gray-400"
                        >
                            · {{ suggestion.confidence }}% confiança
                        </span>
                    </span>
                    <span
                        v-if="suggestion.why"
                        class="text-[12.5px] text-zinc-500 dark:text-gray-400"
                        :class="whyExpanded ? '' : 'line-clamp-3'"
                        :title="whyExpanded ? '' : suggestion.why"
                        @click="whyExpanded = !whyExpanded"
                    >
                        {{ suggestion.why }}
                    </span>
                </div>
                <button
                    v-if="suggestion.key"
                    type="button"
                    class="h-[26px] whitespace-nowrap rounded-md border border-zinc-200 bg-white px-2.5 text-xs font-medium text-zinc-900 hover:bg-zinc-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                    @click="pick(suggestion.key)"
                >
                    Usar
                </button>
            </div>

            <div class="flex flex-col gap-2">
                <button
                    v-for="option in options"
                    :key="option.key"
                    type="button"
                    class="flex items-start gap-2.5 rounded-[10px] border bg-white px-3 py-2.5 text-left transition-shadow dark:bg-gray-800"
                    :class="
                        decision === option.key
                            ? option.activeClass
                            : 'border-zinc-200 hover:border-zinc-300 dark:border-gray-600 dark:hover:border-gray-500'
                    "
                    @click="pick(option.key)"
                >
                    <span
                        class="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border"
                        :class="
                            decision === option.key
                                ? option.radioClass
                                : 'border-zinc-300 dark:border-gray-500'
                        "
                    >
                        <span
                            class="h-2 w-2 rounded-full"
                            :class="
                                decision === option.key ? option.dotClass : ''
                            "
                        ></span>
                    </span>
                    <span class="flex flex-1 flex-col gap-0.5">
                        <span
                            class="text-[13.5px] font-semibold"
                            :class="
                                decision === option.key
                                    ? option.textClass
                                    : 'text-zinc-950 dark:text-white'
                            "
                        >
                            {{ option.label }}
                        </span>
                        <span
                            class="text-[12.5px] text-zinc-500 dark:text-gray-400"
                        >
                            {{ option.hint }}
                        </span>
                    </span>
                    <kbd
                        class="rounded border border-zinc-200 bg-white px-1.5 font-mono text-[11px] text-zinc-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400"
                    >
                        {{ option.shortcut }}
                    </kbd>
                </button>
            </div>

            <div v-if="reasonOptions.length" class="flex flex-col gap-2">
                <span
                    class="text-[13px] font-medium text-zinc-950 dark:text-white"
                >
                    Motivo
                    <span class="font-normal text-zinc-500 dark:text-gray-400">
                        · obrigatório
                    </span>
                </span>
                <div class="flex flex-wrap gap-1.5">
                    <button
                        v-for="reason in reasonOptions"
                        :key="reason"
                        type="button"
                        class="h-7 rounded-full border px-2.5 text-[12.5px] transition-colors"
                        :class="
                            reasons.includes(reason)
                                ? 'border-zinc-900 bg-zinc-900 text-zinc-50 dark:border-white dark:bg-white dark:text-gray-900'
                                : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                        "
                        @click="toggleReason(reason)"
                    >
                        {{ reason }}
                    </button>
                </div>
            </div>

            <div class="flex flex-col gap-1.5">
                <label
                    for="review-note"
                    class="text-[13px] font-medium text-zinc-950 dark:text-white"
                >
                    Observação
                </label>
                <textarea
                    id="review-note"
                    v-model="note"
                    rows="3"
                    placeholder="Visível no histórico do saque…"
                    class="min-h-[72px] resize-y rounded-lg border border-zinc-200 bg-white px-2.5 py-2 text-[13px] text-zinc-950 placeholder:text-zinc-400 focus:border-zinc-400 focus:ring-0 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500"
                ></textarea>
            </div>

            <div class="flex flex-col gap-2">
                <button
                    type="button"
                    class="flex h-[38px] items-center justify-center gap-2.5 rounded-lg text-[13.5px] font-medium transition-colors"
                    :class="
                        canSubmit
                            ? 'bg-zinc-900 text-zinc-50 hover:bg-zinc-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200'
                            : 'cursor-not-allowed bg-zinc-100 text-zinc-400 dark:bg-gray-700 dark:text-gray-500'
                    "
                    :disabled="!canSubmit"
                    @click="submit"
                >
                    {{ submitLabel }}
                    <span class="font-mono text-[11px] opacity-70">
                        {{ isMacOS ? "⌘↵" : "Ctrl↵" }}
                    </span>
                </button>
                <button
                    type="button"
                    class="flex h-[34px] items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white text-[13px] font-medium text-zinc-900 hover:bg-zinc-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                    v-tooltip="
                        'Manda este saque para o fim da fila sem decidir'
                    "
                    @click="$emit('skip')"
                >
                    Pular, decidir depois
                    <span
                        class="font-mono text-[11px] text-zinc-500 dark:text-gray-400"
                    >
                        S
                    </span>
                </button>
            </div>
        </div>
    </section>
</template>

<script>
import { SparklesIcon } from "@heroicons/vue/24/outline";

const OPTIONS = [
    {
        key: "approved",
        label: "Aprovar",
        shortcut: "A",
        hint: "Segue para o banco.",
        submitLabel: "Aprovar saque",
        activeClass: "border-green-600 ring-[3px] ring-green-600/15",
        radioClass: "border-green-600",
        dotClass: "bg-green-600",
        textClass: "text-green-700 dark:text-green-400",
    },
    {
        key: "warning",
        label: "Atenção",
        shortcut: "T",
        hint: "Retém o saque e sinaliza o seller para revisão.",
        submitLabel: "Marcar atenção",
        activeClass: "border-amber-600 ring-[3px] ring-amber-600/15",
        radioClass: "border-amber-600",
        dotClass: "bg-amber-600",
        textClass: "text-amber-700 dark:text-amber-400",
    },
    {
        key: "rejected",
        label: "Reprovar",
        shortcut: "R",
        hint: "Reprova o saque.",
        submitLabel: "Reprovar saque",
        activeClass: "border-red-600 ring-[3px] ring-red-600/15",
        radioClass: "border-red-600",
        dotClass: "bg-red-600",
        textClass: "text-red-700 dark:text-red-400",
    },
];

const REASONS = {
    warning: [
        "Concentração de comissões",
        "Produto recente",
        "Nomes suspeitos",
        "Recusa de cartão alta",
        "Aguardar documentação",
        "Conta bancária alterada",
    ],
    rejected: [
        "Fraude confirmada",
        "Chargeback elevado",
        "Titular divergente",
        "Seller bloqueado",
        "Documentação inválida",
    ],
};

const ANALYSIS_TO_STATUS = {
    Aprovar: "approved",
    approved: "approved",
    Atenção: "warning",
    Reprovar: "rejected",
};

const CHECKLIST_TO_STATUS = {
    approved: "approved",
    review: "warning",
    pending: "warning",
    rejected: "rejected",
    failed: "rejected",
};

export default {
    name: "ReviewDecisionForm",
    components: {
        SparklesIcon,
    },
    props: {
        review: {
            type: Object,
            required: true,
        },
        analysis: {
            type: [Object, Array],
            default: null,
        },
        checklist: {
            type: Object,
            default: null,
        },
    },
    emits: ["submit", "skip"],
    data() {
        return {
            decision: null,
            reasons: [],
            note: "",
            whyExpanded: false,
            options: OPTIONS,
            isMacOS: navigator.platform.toUpperCase().indexOf("MAC") >= 0,
        };
    },
    computed: {
        reasonOptions() {
            return REASONS[this.decision] || [];
        },
        needsReason() {
            return this.reasonOptions.length > 0;
        },
        canSubmit() {
            return (
                !!this.decision &&
                (!this.needsReason || this.reasons.length > 0)
            );
        },
        submitLabel() {
            return (
                OPTIONS.find((option) => option.key === this.decision)
                    ?.submitLabel || "Escolha uma decisão"
            );
        },
        suggestion() {
            const analysis = Array.isArray(this.analysis)
                ? this.analysis[0]
                : this.analysis;

            if (analysis?.analysis) {
                const key = ANALYSIS_TO_STATUS[analysis.analysis] || null;

                return {
                    key,
                    label: key
                        ? OPTIONS.find((option) => option.key === key).label
                        : analysis.analysis,
                    why: analysis.reason || "",
                    confidence: analysis.confidence ?? null,
                };
            }

            const key = CHECKLIST_TO_STATUS[this.checklist?.status];

            if (key && this.checklist?.recommendation) {
                return {
                    key,
                    label: OPTIONS.find((option) => option.key === key).label,
                    why: this.checklist.recommendation,
                    confidence: null,
                };
            }

            return null;
        },
    },
    watch: {
        "review.id"() {
            this.reset();
        },
    },
    methods: {
        reset() {
            this.decision = null;
            this.reasons = [];
            this.note = "";
            this.whyExpanded = false;
        },
        pick(key) {
            if (this.decision === key) {
                return;
            }

            this.decision = key;
            this.reasons = [];
        },
        toggleReason(reason) {
            this.reasons = this.reasons.includes(reason)
                ? this.reasons.filter((item) => item !== reason)
                : [...this.reasons, reason];
        },
        buildContent() {
            const parts = [];

            if (this.reasons.length) {
                parts.push(`Motivo: ${this.reasons.join(", ")}`);
            }

            if (this.note.trim()) {
                parts.push(this.note.trim());
            }

            return parts.join("\n\n");
        },
        submit() {
            if (!this.canSubmit) {
                return;
            }

            this.$emit("submit", {
                status: this.decision,
                content: this.buildContent(),
            });
        },
    },
};
</script>
